import { CognitoUser } from 'amazon-cognito-identity-js';
import { clearActiveChallengeState, setActiveChallengeState } from './challengeState';
import {
  completeDeviceSrpAuth,
  confirmAndRememberDevice,
  getCachedDeviceKey,
  hydrateUserDeviceContext,
  clearDeviceContextFromStorage,
  isCognitoDeviceDoesNotExistError,
} from './deviceTracking';

const parseJson = (value, fallback) => {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
};

export const finalizeSuccessfulAuth = (user, authenticationResult) => {
  user.signInUserSession = user.getCognitoUserSession(authenticationResult);
  user.cacheTokens();
  const cognitoSub = user.signInUserSession.getIdToken().payload.sub;
  const cognitoToken = user.signInUserSession.getAccessToken().getJwtToken();
  return { cognitoSub, token: cognitoToken };
};

const initiatePasswordAuth = ({ userPool, user, email, password, includeDeviceKey = true }) => {
  const jsonReq = {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: userPool.getClientId(),
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
    },
  };

  // If we have a cached Cognito device key, send it so Cognito can treat this as a known device
  // and (based on pool config) suppress MFA.
  const deviceKey = includeDeviceKey ? getCachedDeviceKey(user) : null;
  if (deviceKey) {
    jsonReq.AuthParameters.DEVICE_KEY = deviceKey;
  }

  return new Promise((resolve) => {
    try {
      user.client.request('InitiateAuth', jsonReq, (err, data) => resolve({ err, data }));
    } catch (e) {
      resolve({ err: e, data: null });
    }
  });
};

const toNewPasswordRequiredPayload = ({ email, session, challengeParameters }) => {
  const userAttributes = parseJson(challengeParameters?.userAttributes, null);
  const rawRequiredAttributes = parseJson(challengeParameters?.requiredAttributes, []);
  const requiredAttributes = Array.isArray(rawRequiredAttributes)
    ? rawRequiredAttributes.map((attr) => String(attr).replace(/^userAttributes\./, ''))
    : [];

  return {
    email,
    session,
    userAttributes,
    requiredAttributes,
  };
};

export const cognitoSignInWithPassword = async ({ userPool, email, password }) => {
  const user = new CognitoUser({ Username: email, Pool: userPool });

  const clientId = userPool.getClientId();

  let { err, data } = await initiatePasswordAuth({ userPool, user, email, password, includeDeviceKey: true });
  if (err && isCognitoDeviceDoesNotExistError(err)) {
    // Stale device keys (server-side device was forgotten). Clear and retry without DEVICE_KEY
    // so Cognito falls back to the normal MFA challenge flow.
    clearDeviceContextFromStorage({ clientId, usernames: [email, user?.username] });
    ({ err, data } = await initiatePasswordAuth({ userPool, user, email, password, includeDeviceKey: false }));
  }
  if (err) {
    return { success: false, error: err };
  }

  let challengeName = data?.ChallengeName;
  let challengeParameters = data?.ChallengeParameters || {};

  // DEVICE_SRP_AUTH is not an MFA step; it's Cognito verifying the remembered device.
  // Complete it automatically (no UI) so login succeeds and MFA can be suppressed.
  if (challengeName === 'DEVICE_SRP_AUTH') {
    const session = data?.Session;
    const canonicalUsername = challengeParameters?.USER_ID_FOR_SRP || email;

    // Use canonical username for the challenge, but hydrate device info from any cached namespace.
    user.username = canonicalUsername;
    hydrateUserDeviceContext({ user, clientId, usernames: [canonicalUsername, email] });

    try {
      const cognitoUserSession = await completeDeviceSrpAuth({ user, session });
      clearActiveChallengeState();
      return {
        success: true,
        payload: {
          cognitoSub: cognitoUserSession.getIdToken().payload.sub,
          token: cognitoUserSession.getAccessToken().getJwtToken(),
        },
      };
    } catch (e) {
      // Any DEVICE_SRP_AUTH failure is non-fatal: clear the stale device context and
      // retry InitiateAuth without a device key so Cognito falls back to the normal
      // challenge flow (MFA, NEW_PASSWORD_REQUIRED, etc.).
      clearDeviceContextFromStorage({ clientId, usernames: [canonicalUsername, email] });
      ({ err, data } = await initiatePasswordAuth({ userPool, user, email, password, includeDeviceKey: false }));
      if (err) return { success: false, error: err };
      challengeName = data?.ChallengeName;
      challengeParameters = data?.ChallengeParameters || {};
    }
  }

  if (challengeName) {
    user.Session = data?.Session;

    // Cognito may provide a canonical username to use for challenge responses.
    const challengeUsername = challengeParameters?.USER_ID_FOR_SRP || email;

    setActiveChallengeState({
      user,
      username: challengeUsername,
      challengeName,
      session: user.Session,
    });

    if (challengeName === 'NEW_PASSWORD_REQUIRED') {
      return {
        success: false,
        challenge: 'NEW_PASSWORD_REQUIRED',
        payload: toNewPasswordRequiredPayload({
          email,
          session: user.Session,
          challengeParameters,
        }),
      };
    }

    return {
      success: false,
      challenge: challengeName,
      payload: {
        email,
        session: user.Session,
        challengeParameters,
      },
    };
  }

  try {
    const authenticationResult = data.AuthenticationResult;
    const payload = finalizeSuccessfulAuth(user, authenticationResult);
    await confirmAndRememberDevice({ user, authenticationResult });
    clearActiveChallengeState();
    return { success: true, payload };
  } catch (e) {
    return { success: false, error: e };
  }
};
